// attuneDiagnosticsService — capture for the Attune Hook + Experience modes.
//
// Writes one row per (enrollment_id, slide_id) into attune_diagnostics, upserted
// in place. Hook stores mode + transcript (the personalised opener is the final
// assistant turn); Experience additionally fills the DiagnosticData columns +
// chosen modality + reflection + responses (Phase 4). Best-effort + behind the
// fk_attune_capture kill-switch, like the gyd capture. Tenant resolved from the
// enrollment, never trusted from the client.

import { supabase } from '../lib/supabase';
import { isAttuneCaptureEnabled } from './gydFacilitationService';
import { MODALITIES, type ModalityKey } from '../lib/scenario-builder/modality';

export interface DiagnosticTurn {
  role: 'user' | 'assistant';
  content: string;
}

/** The structured fields Attune's diagnostic surfaces (Experience mode). */
export interface DiagnosticData {
  learningFocus?: string | null;
  realContext?: string | null;
  keyGap?: string | null;
  suggestedModality?: string | null;
  suggestedVisualStyle?: string | null;
  tonePace?: string | null;
  motivationHook?: string | null;
}

export interface RecordDiagnosticInput {
  /** Enrollment + tenant resolved internally from courseId + current user. */
  courseId: string;
  lessonId: string;
  slideId: string;
  mode: 'hook' | 'experience';
  transcript: DiagnosticTurn[];
  /** Experience mode — the extracted diagnostic columns. */
  diagnostic?: DiagnosticData;
  chosenModality?: string | null;
  reflection?: string | null;
  experienceResponses?: unknown;
}

export interface RecordDiagnosticResult {
  written: boolean;
  skipReason?: string;
}

export async function recordDiagnostic(
  input: RecordDiagnosticInput,
): Promise<RecordDiagnosticResult> {
  if (!(await isAttuneCaptureEnabled())) return { written: false, skipReason: 'flag-off' };

  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return { written: false, skipReason: 'not-authenticated' };

  const { data: enrollmentRow, error: enrollmentErr } = await supabase
    .from('enrollments')
    .select('id, tenant_id')
    .eq('user_id', user.id)
    .eq('course_id', input.courseId)
    .maybeSingle();
  if (enrollmentErr || !enrollmentRow?.id || !enrollmentRow.tenant_id) {
    return { written: false, skipReason: 'enrollment-not-found' };
  }

  const d = input.diagnostic ?? {};
  const row = {
    tenant_id: enrollmentRow.tenant_id,
    enrollment_id: enrollmentRow.id,
    lesson_id: input.lessonId,
    slide_id: input.slideId,
    mode: input.mode,
    learning_focus: d.learningFocus ?? null,
    real_context: d.realContext ?? null,
    key_gap: d.keyGap ?? null,
    suggested_modality: d.suggestedModality ?? null,
    suggested_visual_style: d.suggestedVisualStyle ?? null,
    tone_pace: d.tonePace ?? null,
    motivation_hook: d.motivationHook ?? null,
    chosen_modality: input.chosenModality ?? null,
    reflection: input.reflection ?? null,
    transcript: input.transcript,
    experience_responses: input.experienceResponses ?? null,
    updated_at: new Date().toISOString(),
  };

  const { error } = await supabase
    .from('attune_diagnostics')
    .upsert(row, { onConflict: 'enrollment_id,slide_id' });
  if (error) return { written: false, skipReason: `upsert-error:${error.code}` };

  return { written: true };
}

export interface DiagnosticRecord {
  id: string;
  enrollment_id: string;
  lesson_id: string;
  slide_id: string;
  mode: 'hook' | 'experience';
  learning_focus: string | null;
  real_context: string | null;
  key_gap: string | null;
  suggested_modality: string | null;
  suggested_visual_style: string | null;
  tone_pace: string | null;
  motivation_hook: string | null;
  chosen_modality: string | null;
  reflection: string | null;
  transcript: DiagnosticTurn[];
  experience_responses: unknown;
  created_at: string;
  updated_at: string;
}

// ─── Cross-slide memory (learner continuity) ──────────────────────────────
// So a later Attune moment can "remember" earlier ones in the SAME course and
// feel like one assistant who's been with the learner — rather than a stranger
// each time. Reads the learner's own prior rows (RLS-scoped), folds them into a
// compact prose blob, and the slide injects that into the system prompt + a
// personalised opener. Course-scoped via the enrollment — except the optional
// profile narrative (includeProfile), which is the Learner Model's deliberately
// cross-course "who they are" lead.

/** Minimal row shapes the formatter needs (kept narrow for unit-testability). */
export interface PriorDiagnosticRow {
  learning_focus: string | null;
  real_context: string | null;
  key_gap: string | null;
  chosen_modality: string | null;
  reflection: string | null;
  updated_at: string;
}
export interface PriorFacilitationRow {
  wrapup_insight: string | null;
  updated_at: string;
}

function clip(s: string | null | undefined, n: number): string | null {
  if (!s) return null;
  const t = s.trim();
  if (!t) return null;
  return t.length > n ? `${t.slice(0, n - 1).trimEnd()}…` : t;
}

/**
 * Fold prior Attune rows into a compact "what you remember about this learner"
 * blob, oldest-first, for the system prompt. Pure (no IO) so it's unit-testable.
 * Returns null when there's nothing worth remembering.
 *
 * When a synthesised profile narrative is available (the Learner Model), it
 * leads the blob as the standing "who they are" — the ambient-warmth swap from
 * LEARNER_MODEL_PLAN.md — with the course-scoped moments as the specifics.
 */
export function formatAttuneMemory(
  diagnostics: PriorDiagnosticRow[],
  facilitations: PriorFacilitationRow[],
  opts: { maxItems?: number; profileNarrative?: string | null } = {},
): string | null {
  const items: { when: string; line: string }[] = [];

  for (const r of diagnostics) {
    const parts: string[] = [];
    const focus = clip(r.learning_focus, 160);
    const ctx = clip(r.real_context, 160);
    const gap = clip(r.key_gap, 160);
    const refl = clip(r.reflection, 220);
    if (focus) parts.push(`they wanted to get sharper at ${focus}`);
    if (ctx) parts.push(`what's really going on for them: ${ctx}`);
    if (gap) parts.push(`the real gap was ${gap}`);
    if (r.chosen_modality) {
      const label = MODALITIES[r.chosen_modality as ModalityKey]?.label ?? r.chosen_modality;
      parts.push(`they chose to learn by ${label.toLowerCase()}`);
    }
    if (refl) parts.push(`afterwards they reflected: "${refl}"`);
    if (parts.length) items.push({ when: r.updated_at, line: parts.join('; ') });
  }

  for (const r of facilitations) {
    const insight = clip(r.wrapup_insight, 220);
    if (insight) items.push({ when: r.updated_at, line: `an insight they reached: "${insight}"` });
  }

  const narrative = clip(opts.profileNarrative, 500);
  if (!items.length && !narrative) return null;
  items.sort((a, b) => a.when.localeCompare(b.when)); // oldest-first → a timeline
  const max = opts.maxItems ?? 6;
  const kept = items.length > max ? items.slice(items.length - max) : items;
  const bullets = kept.map((i) => `- ${i.line}`).join('\n');
  if (!narrative) return bullets;
  const lead = `Who they are as a learner (their standing story): ${narrative}`;
  return kept.length ? `${lead}\n${bullets}` : lead;
}

export interface AttuneMemoryInput {
  courseId: string;
  /** The current slide — excluded so a re-entered moment doesn't echo itself. */
  slideId: string;
  /** How many of each (diagnostics, facilitations) to pull before merging. */
  limit?: number;
  /**
   * Weave in the learner's synthesised profile narrative (own row, RLS-scoped).
   * Pass the tenant's `memory` feature here — the narrative is the one
   * deliberately cross-course ingredient; everything else stays course-scoped.
   */
  includeProfile?: boolean;
}

/**
 * Course-scoped memory for the current learner: prior Attune diagnostics +
 * facilitation wrap-ups in this enrollment, formatted for the prompt. Returns
 * null when not signed in, no enrollment, nothing captured, or on any error —
 * memory is best-effort and must never break the moment.
 */
export async function getPriorAttuneMemory(input: AttuneMemoryInput): Promise<string | null> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return null;

    const { data: enrollmentRow } = await supabase
      .from('enrollments')
      .select('id')
      .eq('user_id', user.id)
      .eq('course_id', input.courseId)
      .maybeSingle();
    if (!enrollmentRow?.id) return null;

    const limit = input.limit ?? 4;
    const [{ data: diagRows }, { data: facRows }, profileRes] = await Promise.all([
      supabase
        .from('attune_diagnostics')
        .select('learning_focus, real_context, key_gap, chosen_modality, reflection, updated_at')
        .eq('enrollment_id', enrollmentRow.id)
        .neq('slide_id', input.slideId)
        .order('updated_at', { ascending: false })
        .limit(limit),
      supabase
        .from('gyd_facilitations')
        .select('wrapup_insight, updated_at')
        .eq('enrollment_id', enrollmentRow.id)
        .neq('slide_id', input.slideId)
        .not('wrapup_insight', 'is', null)
        .order('updated_at', { ascending: false })
        .limit(limit),
      input.includeProfile
        ? supabase.from('learner_profiles').select('narrative').eq('user_id', user.id).maybeSingle()
        : Promise.resolve({ data: null }),
    ]);

    return formatAttuneMemory(
      (diagRows ?? []) as PriorDiagnosticRow[],
      (facRows ?? []) as PriorFacilitationRow[],
      { profileNarrative: (profileRes.data as { narrative: string | null } | null)?.narrative ?? null },
    );
  } catch {
    return null;
  }
}

/**
 * Admin read: diagnostic records for the caller's tenant (RLS-scoped), most
 * recent first. Returns [] on error.
 */
export async function listDiagnostics(limit = 200): Promise<DiagnosticRecord[]> {
  const { data, error } = await supabase
    .from('attune_diagnostics')
    .select('*')
    .order('created_at', { ascending: false })
    .limit(limit);
  if (error) return [];
  return (data ?? []) as DiagnosticRecord[];
}
